import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import { FIT_SIGNALS, HOME_INDUSTRIES } from "@/data/marketing/homepage";
import { getIndustryHref } from "@/config/site";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

export default function HomeIndustries() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  return (
    <section className="w-full bg-white py-16 sm:py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Who it's for"
          title={
            <>
              Built for any business answering the same questions{" "}
              <GradientText>all day</GradientText>
            </>
          }
          subtitle="It doesn't matter what you sell. If this is your day, a genie fits."
        />

        {/* The situation, not the industry. Four signals every SME recognises. */}
        <div
          ref={ref}
          className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {FIT_SIGNALS.map((signal, i) => (
            <div
              key={signal}
              className={cn(
                "flex items-start gap-3 rounded-2xl border border-[#E5EAF0] bg-[#F9FAFC] p-5 transition-all duration-500 ease-out",
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              )}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-genie-primary/10">
                <GenieIcon
                  name="check-circle"
                  size={16}
                  color="#4E9CFF"
                  secondaryColor="#FF4DB8"
                  gradientEnabled
                  label=""
                />
              </span>
              <p className="text-base font-medium leading-snug text-[#222B45]">
                {signal}
              </p>
            </div>
          ))}
        </div>

        {/* Industries demoted to examples of the pattern — a light linked strip, not a catalogue. */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            That's a plumber, a car yard, a real estate office, a manufacturer,
            a hotel. Already built for:
          </p>
          <div className="mx-auto mt-4 flex max-w-3xl flex-wrap justify-center gap-2">
            {HOME_INDUSTRIES.map((industry) => (
              <Link
                key={industry.slug}
                to={getIndustryHref(industry.slug)}
                className="rounded-full border border-[#E5EAF0] bg-white px-3.5 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-genie-primary/40 hover:text-genie-primary"
              >
                {industry.name}
              </Link>
            ))}
          </div>
          <Link
            to="/help-genie-for-business-solutions"
            className="mt-6 inline-flex items-center text-sm font-semibold text-genie-primary hover:text-genie-secondary transition-colors"
          >
            Explore all solutions
            <ChevronRight className="ml-0.5 h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
